import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtTL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import {
  Users, FileClock, AlertTriangle, Wallet, TrendingUp, BookOpen, ListChecks,
  ArrowRight, Plus, ChevronDown, Mail, UserPlus, FileText, BarChart3, Wallet as WalletIcon,
  DollarSign, Euro, Coins, Eye, EyeOff, CalendarClock,
} from "lucide-react";

function fmtRate(v) {
  if (v == null) return "—";
  return v.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " ₺";
}

function RateCard({ icon: Icon, label, data, tint, iconColor }) {
  return (
    <Card className="p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg grid place-items-center shrink-0" style={{ backgroundColor: tint }}>
        <Icon size={18} style={{ color: iconColor }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</div>
        {data ? (
          <div className="flex items-baseline gap-3 mt-0.5">
            <span className="text-sm"><span className="text-muted-foreground">Alış</span> <b className="font-head">{fmtRate(data.alis)}</b></span>
            <span className="text-sm"><span className="text-muted-foreground">Satış</span> <b className="font-head">{fmtRate(data.satis)}</b></span>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground mt-0.5">Veri alınamadı</div>
        )}
      </div>
    </Card>
  );
}

const NAVY = "#0B2344";
const DONUT_COLORS = ["#0B2344", "#0d9488", "#f59e0b", "#8b5cf6"];
const MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

function periodLabel(p) {
  if (!p || p.length < 7) return p || "-";
  const m = parseInt(p.slice(5, 7), 10);
  return `${MONTHS[m - 1] || ""} ${p.slice(0, 4)} Dönemi`;
}
function dateLabel(iso) {
  if (!iso) return "-";
  return iso.split("-").reverse().join(".");
}

function Kpi({ icon: Icon, label, value, sub, onClick, tint, iconColor, maskable }) {
  const storageKey = `kpi_hide_${label}`;
  const [hidden, setHidden] = useState(() => {
    if (!maskable) return false;
    try { return localStorage.getItem(storageKey) === "1"; } catch { return false; }
  });
  const toggleHidden = (e) => {
    e.stopPropagation();
    setHidden((h) => {
      const next = !h;
      try { localStorage.setItem(storageKey, next ? "1" : "0"); } catch {}
      return next;
    });
  };
  return (
    <Card
      onClick={onClick}
      data-testid={`kpi-${label}`}
      className={`p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md border-border ${onClick ? "cursor-pointer" : "cursor-default"}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="font-head text-3xl font-semibold mt-2 tracking-tight">{maskable && hidden ? "••••••" : value}</div>
          {sub && <div className="text-xs text-muted-foreground mt-1">{maskable && hidden ? "••••" : sub}</div>}
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ backgroundColor: tint }}>
            <Icon size={18} style={{ color: iconColor }} />
          </div>
          {maskable && (
            <button
              type="button"
              onClick={toggleHidden}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label={hidden ? "Tutarı göster" : "Tutarı gizle"}
              data-testid={`kpi-mask-toggle-${label}`}
            >
              {hidden ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

const QUICK_ACTIONS = [
  { label: "Mükellef Ekle", to: "/mukellefler?yeni=1", icon: UserPlus, tint: "#DBEAFE", iconColor: "#1D4ED8" },
  { label: "Görev Oluştur", to: "/gorevler", icon: ListChecks, tint: "#F1F5F9", iconColor: "#334155" },
  { label: "e-Tebligat Kontrol", to: "/etebligat", icon: Mail, tint: "#FCE7F3", iconColor: "#BE185D" },
  { label: "Cari Hesap Ekle", to: "/cari", icon: WalletIcon, tint: "#D1FAE5", iconColor: "#047857" },
  { label: "Beyanname Ekle", to: "/beyannameler", icon: FileText, tint: "#FEF3C7", iconColor: "#B45309" },
  { label: "Rapor Oluştur", to: "/raporlar", icon: BarChart3, tint: "#EDE9FE", iconColor: "#6D28D9" },
];

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [rates, setRates] = useState(null);
  const [calEvents, setCalEvents] = useState([]);
  const nav = useNavigate();
  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data));
    api.get("/market-rates").then((r) => setRates(r.data)).catch(() => {});
    api.get("/calendar").then((r) => setCalEvents(r.data)).catch(() => {});
  }, []);
  if (!d) return <div className="text-muted-foreground">Yükleniyor...</div>;

  const tomorrowStr = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const tomorrowEvents = calEvents.filter((e) => e.date === tomorrowStr);

  const pieData = Object.entries(d.by_type).map(([k, v]) => ({ name: k, value: v }));
  const pieTotal = pieData.reduce((a, p) => a + p.value, 0);

  const upcomingGrouped = Object.values(
    (d.upcoming || []).reduce((acc, u) => {
      const key = `${u.type}|${u.period}|${u.deadline}`;
      if (!acc[key]) acc[key] = { type: u.type, period: u.period, deadline: u.deadline, days: u.days, count: 0 };
      acc[key].count += 1;
      acc[key].days = Math.min(acc[key].days, u.days);
      return acc;
    }, {})
  ).sort((a, b) => a.days - b.days).slice(0, 6);

  const statusBadge = (days) => {
    if (days < 0) return { text: "Gecikti", cls: "bg-rose-100 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400" };
    if (days <= 7) return { text: "Yaklaşıyor", cls: "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400" };
    return { text: "Normal", cls: "bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400" };
  };

  const summaryRows = [
    { label: "Beyanname bekleyen mükellef", value: d.pending_clients, icon: FileClock, tint: "#FEF3C7", iconColor: "#B45309", to: "/beyannameler" },
    { label: "e-Defter berat bekleyen", value: `${d.edefter_eksik}/${d.edefter_total}`, icon: BookOpen, tint: "#EDE9FE", iconColor: "#6D28D9", to: "/edefter" },
    { label: "Yeni e-Tebligat", value: d.etebligat_unread, icon: Mail, tint: "#FCE7F3", iconColor: "#BE185D", to: "/etebligat" },
    { label: "Gecikmiş görev", value: d.overdue_tasks, icon: AlertTriangle, tint: "#FFE4E6", iconColor: "#BE123C", to: "/gorevler" },
    { label: "Borcu olan mükellef", value: d.clients_with_debt, icon: WalletIcon, tint: "#D1FAE5", iconColor: "#047857", to: "/cari" },
  ];

  return (
    <div className="space-y-6 fade-in">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head text-3xl font-semibold tracking-tight">Genel Bakış</h1>
          <p className="text-sm text-muted-foreground mt-1">Ofisinizin güncel durum özeti</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                Hızlı İşlemler <ChevronDown size={15} className="ml-1.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {QUICK_ACTIONS.map((a) => (
                <DropdownMenuItem key={a.label} onClick={() => nav(a.to)}>
                  <a.icon size={15} className="mr-2" /> {a.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button style={{ backgroundColor: NAVY }} className="text-white hover:opacity-90" onClick={() => nav("/mukellefler?yeni=1")}>
            <Plus size={16} className="mr-1.5" /> Yeni Mükellef
          </Button>
        </div>
      </div>

      {tomorrowEvents.length > 0 && (
        <Card
          data-testid="tomorrow-events-card"
          className="p-4 border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 cursor-pointer"
          onClick={() => nav("/takvim")}
        >
          <div className="flex items-center gap-2 mb-2">
            <CalendarClock size={16} className="text-amber-600 dark:text-amber-400" />
            <h3 className="font-head font-medium text-sm">Yarın ({dateLabel(tomorrowStr)}) için hatırlatmalar</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {tomorrowEvents.map((e) => (
              <span
                key={e.id}
                className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                  e.kind === "deadline"
                    ? "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400"
                    : "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400"
                }`}
              >
                {e.title}
              </span>
            ))}
          </div>
        </Card>
      )}

      {rates && (
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <RateCard icon={DollarSign} label="Dolar (USD)" data={rates.usd} tint="#D1FAE5" iconColor="#047857" />
            <RateCard icon={Euro} label="Euro (EUR)" data={rates.eur} tint="#DBEAFE" iconColor="#1D4ED8" />
            <RateCard icon={Coins} label="Gram Altın" data={rates.gram_altin} tint="#FEF3C7" iconColor="#B45309" />
          </div>
          <p className="text-[11px] text-muted-foreground mt-1.5">
            Kaynak: TCMB (Dolar/Euro){rates.tcmb_tarihi ? ` · ${rates.tcmb_tarihi}` : ""}, Altınkaynak (Gram Altın)
            {rates.altin_guncelleme ? ` · ${rates.altin_guncelleme}` : ""}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Users} label="Aktif Mükellef" value={d.total_clients} sub="Toplam kayıtlı" onClick={() => nav("/mukellefler")} tint="#DBEAFE" iconColor="#1D4ED8" />
        <Kpi icon={Wallet} label="Aylık Sözleşme Geliri" value={fmtTL(d.monthly_contract_income)} sub="Aktif mükellef ücret toplamı" tint="#D1FAE5" iconColor="#047857" maskable />
        <Kpi icon={FileClock} label="Bekleyen Beyanname" value={Object.values(d.pending_by_type).reduce((a, b) => a + b, 0)} sub="Bu dönem tamamlanmadı" onClick={() => nav("/beyannameler")} tint="#FEF3C7" iconColor="#B45309" />
        <Kpi icon={BookOpen} label="Eksik e-Defter" value={`${d.edefter_eksik}/${d.edefter_total}`} sub="Berat tamamlanmadı" onClick={() => nav("/edefter")} tint="#EDE9FE" iconColor="#6D28D9" />
        <Kpi icon={Wallet} label="Toplam Alacak" value={fmtTL(d.toplam_alacak)} sub="Tahsil edilmemiş" onClick={() => nav("/cari")} tint="#FFE4E6" iconColor="#BE123C" maskable />
        <Kpi icon={TrendingUp} label="Bu Ay Tahsilat" value={fmtTL(d.this_month_collected)} sub={`Tahakkuk: ${fmtTL(d.this_month_accrued)}`} onClick={() => nav("/tahsilatlar")} tint="#D1FAE5" iconColor="#047857" maskable />
        <Kpi icon={ListChecks} label="Açık Görev" value={d.open_tasks} sub={`${d.overdue_tasks} gecikmiş`} onClick={() => nav("/gorevler")} tint="#DBEAFE" iconColor="#1D4ED8" />
        <Kpi icon={AlertTriangle} label="Gecikmiş Görev" value={d.overdue_tasks} sub="Acil aksiyon" onClick={() => nav("/gorevler")} tint="#FFE4E6" iconColor="#BE123C" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-head font-medium">Bekleyen İşlerin Özeti</h3>
          </div>
          <div className="space-y-1">
            {summaryRows.map((r) => (
              <div key={r.label} onClick={() => nav(r.to)}
                className="flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-accent cursor-pointer transition-colors">
                <div className="w-8 h-8 rounded-lg grid place-items-center shrink-0" style={{ backgroundColor: r.tint }}>
                  <r.icon size={15} style={{ color: r.iconColor }} />
                </div>
                <span className="text-sm flex-1">{r.label}</span>
                <span className="text-sm font-semibold">{r.value}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-head font-medium">Son e-Tebligatlar</h3>
            <button onClick={() => nav("/etebligat")} className="text-xs font-medium text-primary hover:underline">Tümünü Gör</button>
          </div>
          {(!d.recent_etebligat || d.recent_etebligat.length === 0) ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Henüz e-Tebligat kaydı yok.</p>
          ) : (
            <div className="space-y-3">
              {d.recent_etebligat.map((e, i) => {
                const okundu = e.okunma_durumu === "Okundu";
                return (
                  <div key={i} className="flex items-start justify-between gap-2 pb-3 border-b border-border last:border-0 last:pb-0">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{e.konu || e.belge_turu || "—"}</div>
                      <div className="text-xs text-muted-foreground truncate">{e.gonderen || "—"}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">{(e.teblig_tarihi || "").slice(0, 16)}</div>
                    </div>
                    <span className={`shrink-0 text-[10px] font-semibold px-2 py-1 rounded ${okundu ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400" : "bg-rose-100 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400"}`}>
                      {e.okunma_durumu || "—"}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="p-5">
          <h3 className="font-head font-medium mb-4">Mükellef Dağılımı</h3>
          <div className="relative">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={78} paddingAngle={3}>
                  {pieData.map((_, i) => <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 top-0 flex flex-col items-center justify-center pointer-events-none" style={{ height: 200 }}>
              <div className="text-[11px] text-muted-foreground">Toplam</div>
              <div className="font-head text-2xl font-semibold">{pieTotal}</div>
            </div>
          </div>
          <div className="space-y-1.5 mt-3">
            {pieData.map((p, i) => (
              <div key={p.name} className="flex items-center gap-2 text-xs">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                <span className="flex-1">{p.name}</span>
                <span className="font-medium">{p.value}</span>
                <span className="text-muted-foreground w-12 text-right">{pieTotal ? `%${(p.value / pieTotal * 100).toFixed(1)}` : "%0"}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-head font-medium">Yaklaşan Son Tarihler</h3>
            <button onClick={() => nav("/beyannameler")} className="text-xs font-medium text-primary hover:underline">Tümünü Gör</button>
          </div>
          {upcomingGrouped.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Yaklaşan kritik son tarih yok.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left font-medium py-2">Tarih</th>
                    <th className="text-left font-medium py-2">İşlem</th>
                    <th className="text-left font-medium py-2">Açıklama</th>
                    <th className="text-left font-medium py-2">Mükellef</th>
                    <th className="text-left font-medium py-2">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {upcomingGrouped.map((g, i) => {
                    const b = statusBadge(g.days);
                    return (
                      <tr key={i} className="border-t border-border">
                        <td className="py-2.5 whitespace-nowrap">{dateLabel(g.deadline)}</td>
                        <td className="py-2.5 font-medium whitespace-nowrap">{g.type}</td>
                        <td className="py-2.5 text-muted-foreground whitespace-nowrap">{periodLabel(g.period)}</td>
                        <td className="py-2.5 whitespace-nowrap">{g.count} Mükellef</td>
                        <td className="py-2.5"><span className={`text-xs px-2 py-0.5 rounded ${b.cls}`}>{b.text}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card className="p-5">
          <h3 className="font-head font-medium mb-4">Hızlı Erişim</h3>
          <div className="grid grid-cols-2 gap-2.5">
            {QUICK_ACTIONS.map((a) => (
              <button
                key={a.label}
                onClick={() => nav(a.to)}
                className="flex flex-col items-start gap-2 p-3 rounded-lg border border-border hover:bg-accent transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-lg grid place-items-center" style={{ backgroundColor: a.tint }}>
                  <a.icon size={15} style={{ color: a.iconColor }} />
                </div>
                <span className="text-xs font-medium leading-tight">{a.label}</span>
              </button>
            ))}
          </div>
        </Card>
      </div>

      {d.top_debtors.length > 0 && (
        <Card className="p-5">
          <h3 className="font-head font-medium mb-4">En Borçlu Mükellefler</h3>
          <div className="space-y-1">
            {d.top_debtors.map((t) => (
              <div key={t.client_id} onClick={() => nav(`/mukellefler/${t.client_id}`)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-accent cursor-pointer transition-colors">
                <span className="text-sm font-medium flex-1">{t.unvan}</span>
                <span className="text-sm font-semibold text-rose-600 dark:text-rose-400">{fmtTL(t.bakiye)}</span>
                <ArrowRight size={14} className="text-muted-foreground" />
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
